import { useState, useEffect } from 'react';
import TranslationText from '@Components/Utilities/TranslationText';
import translator from '@Components/Utilities/Translator';
import { useWindowSize } from '@Components/Utilities/WindowSize';
import atoms from '@Components/Utilities/SandhillsAtoms';
import Button from '@mui/material/Button';
import { useRecoilValue } from 'recoil';
import DealerLocationsModal from '@Components/Shared/Modals/DealerLocationsModal';
import GoodleMapsLocationModal from '@Components/Shared/Modals/GoogleMapsLocationModal';
import DescriptiveTextModal from '@Components/Shared/Modals/DescriptiveTextModal';
import AuctionInformationModal from '@Components/Shared/Modals/AuctionInformationModal';
import ConsignmentPackageInfoModal from '@Components/Shared/Modals/ConsignmentPackageInfoModal';
import { sandhillsRequest } from '@Components/Utilities/SandhillsUtility';
import { inquiryType, contactType } from '@Components/Utilities/SandhillsConstants';

const AdditionalPageInformation = (props) => {
    const windowSize = useWindowSize();
    const routes = useRecoilValue(atoms.routes);
    const [showDescription, setShowDescription] = useState(false);
    const [showMap, setShowMap] = useState(false);
    const [showLocations, setShowLocations] = useState(false);
    const dealerLocation = {
        lat: props.GoogleAPIData?.PinLocation?.Latitude,
        lng: props.GoogleAPIData?.PinLocation?.Longitude
    };
    const [biddingAgreementData, setBiddingAgreementData] = useState(null);
    const [showAuctionInfo, setShowAuctionInfo] = useState(false);
    const [showConsignmentDetails, setShowConsignmentDetails] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isSavedToWatchList, setIsSavedToWatchList] = useState(props.IsSavedToWatchList);
    const [aspectRatio, setAspectRatio] = useState(0);

    useEffect(() => {
        if (props.OpenSAMOnLoad) {
            props.openEmailModal(inquiryType.Email, contactType.Retail, {
                DealerCRMID: props.OverrideContactCRMID,
                BranchName: (props.EventTitle ? props.EventTitle : props.Name),
                Subject: (props.SubjectOverride ? props.SubjectOverride : (translator.translate('sandhills.tradesites.mvc.generalinquiryto') + " " + (props.EventTitle ? props.EventTitle : props.Name))),
                ModalTitle: (props.SubjectOverride ? props.SubjectOverride : (translator.translate('sandhills.tradesites.mvc.generalinquiryto') + " " + (props.EventTitle ? props.EventTitle : props.Name))),
                IsGeneralOverride: true
            });
        }
    }, [])

    useEffect(() => {
        const img = new Image();
        img.src = props.LogoUrl;
        img.onload = () => {
            const imgRatio = img.width / img.height;
            setAspectRatio(imgRatio);
        };
    }, [props.LogoUrl]);

    const hasDealerButtons = () => {
        return props.CompanyURL
            || props.IsLiveOrUpcomingEvent
            || (props.OtherLocations && props.OtherLocations.length > 0)
            || props.EventID || props.WebsiteURL || props.LiveEventUrl || props.BilledUser
            || props.DealerAuctionTimeBoardUrl || props.ConsignmentPackageAuctionTimeBoardUrl || props.StandaloneAuctionBoardUrl;
    };

    const onBiddingAgreementDataUpdated = (biddingAgreementDataUpdated) => {
        setBiddingAgreementData(biddingAgreementDataUpdated);
        props.openBiddingAgreementModal?.({
            biddingAgreement: biddingAgreementDataUpdated,
            onBiddingAgreementDataUpdated: onBiddingAgreementDataUpdated
        });
    };

    const loadEventBiddingAggreement = () => {
        if (!isLoading) {
            if (biddingAgreementData) {
                props.openBiddingAgreementModal?.({
                    biddingAgreement: biddingAgreementData,
                    onBiddingAgreementDataUpdated: onBiddingAgreementDataUpdated
                });
            } else {
                setIsLoading(true);
                sandhillsRequest({
                    url: routes.GetBiddingAgreement.replace("{0}", props.EventID),
                    errorHandling: {
                        showDetails: true,
                        useConsole: false,
                        useAlert: true
                    }
                }).then((data) => {
                    if (data?.BidAgreement) {
                        setBiddingAgreementData(data);
                        setIsLoading(false);
                        props.openBiddingAgreementModal?.({
                            biddingAgreement: data,
                            onBiddingAgreementDataUpdated: onBiddingAgreementDataUpdated
                        });
                    } else {
                        alert('An error occurred while retrieving the Terms and Conditions.');
                    }
                }).catch((error) => {
                    setIsLoading(false);
                });
            }
        }
    };

    const removeFromWatchlist = () => {
        sandhillsRequest({
            url: routes.EventWatchlistRemove,
            method: 'post',
            data: {
                eventID: props.EventID
            },
            errorHandling: {
                showDetails: false,
                useConsole: false
            }
        }).then((data) => {
            if (data) {
                setIsSavedToWatchList(false);
            }
        });
    }

    const addToWatchlist = () => {
        sandhillsRequest({
            url: routes.EventWatchlistAdd,
            method: 'post',
            data: {
                eventID: props.EventID
            },
            errorHandling: {
                showDetails: false,
                useConsole: false
            }
        }).then((data) => {
            if (!data.IsAuthenticated) {
                window.location.href = routes.SSO + '?to=' + encodeURIComponent('https://' + window.location.hostname + routes.EventWatchlistAdd + '?EventID=' + props.EventID + '&to=' + encodeURIComponent(window.location.href));
            } else if (data) {
                setIsSavedToWatchList(true);
            }
        });
    }

    return (
        <span className='additional-page-information'>
            <div className='info-container'>
                {!windowSize.isMobile && props.LogoUrl &&
                    <div className={'dealer-image' + (aspectRatio > 1.25 ? '' : ' tall')}>
                        {props.AuctioneerInformationUrl ? <a href={props.AuctioneerInformationUrl}><img src={props.LogoUrl} alt={props.Name} /></a> : <img src={props.LogoUrl} alt={props.Name} /> }
                        {props.EventID && props.WebsiteURL &&
                            <div className='contact-info'>
                                <a id='dealer-header-website-link' href={props.WebsiteURL} target='_blank' aria-label={props.Name + " (" + translator.translate('sandhills.opensinanewtab') + ")"}>
                                    {props.Name}
                                </a>
                            </div>
                        }
                        {props.AuctioneerInformationUrl &&
                            <a className='auctioneer-details' href={props.AuctioneerInformationUrl}>{props.Name}</a>}
                    </div>
                }
                <div className={(!hasDealerButtons() || !props.LogoUrl) ? 'dealer-info fill' : 'dealer-info'}>
                    {props.InventoryManagementURL && <a href={props.InventoryManagementURL} target='_blank' aria-label={"View in Inventory Management (" + translator.translate('sandhills.opensinanewtab') + ")"}>View in Inventory Management</a>}
                    <h3>
                        {props.WebsiteURL ?
                            <a id='dealer-header-website-link' href={props.WebsiteURL} target='_blank' aria-label={(props.EventTitle ? props.EventTitle : props.Name) + " (" + translator.translate('sandhills.opensinanewtab') + ")"}>{props.EventTitle ? props.EventTitle : props.Name}</a> :
                            <span>{props.EventTitle ? <h1>{props.EventTitle}</h1> : props.Name}</span>}
                    </h3>
                    {!props.isConsignmentPackage && !props.isStandaloneSale ?
                        <>
                            <span className='location-group'><b>{translator.translate('sandhills.location')}:&nbsp;</b>
                                {props.ShowStreetAddress && <div className='contact-info'>{props.Address1}&nbsp;</div>}
                                <div className='contact-info'>{props.LocationAddress}</div></span>
                            {props.BusinessPhone &&
                                <div className='phone-section'>
                                    <span><strong><TranslationText tKey='sandhills.phone' />: </strong></span>
                                    <a id='dealer-header-phone-link' href={'tel:' + props.UnformattedBusinessPhone} className='dealer-phone'>
                                        <strong>{props.BusinessPhone}</strong>
                                    </a>
                                </div>
                            }
                            {props.ContactName && !props.EventID &&
                                <div className='contact-info'>
                                    <span><strong><TranslationText tKey='sandhills.contact' />:&nbsp;</strong></span>
                                    {props.ContactName}
                                </div>}
                        </>
                        :
                        <>
                            {props.Description &&
                                <span className='consignment-description'>{props.Description}</span>}
                        </>}
                    {((props.CompanyDescription || props.Description) || (dealerLocation && dealerLocation.lat && dealerLocation.lng) || (props.DocumentList && props.DocumentList.length > 0)) &&
                        <>
                            <div className='link-group'>
                                {(props.CompanyDescription || props.Description) && !props.EventID && !props.isConsignmentPackage &&
                                    <span className='details-link link'>
                                    <div role='button' tabIndex='0' onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setShowDescription(!showDescription); }}} className='details' onClick={() => { setShowDescription(!showDescription) }}>{translator.translate('sandhills.tradepubwebsite.mvc.company')} {translator.translate('sandhills.tradepubwebsite.mvc.details')}<i className='fas fa-external-link-alt' /></div>
                                    </span>
                                }
                                {props.EventID &&
                                    <span className='details-link link'>
                                    <div role='button' tabIndex='0' onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setShowAuctionInfo(!showAuctionInfo); }}} className='details' onClick={() => { setShowAuctionInfo(!showAuctionInfo) }}>{translator.translate('sandhills.tradepubwebsite.mvc.auction')} {translator.translate('sandhills.tradepubwebsite.mvc.details')}<i className='fas fa-external-link-alt' /></div>
                                    </span>
                                }
                                {dealerLocation && dealerLocation.lat && dealerLocation.lng &&
                                    <span className='map-link link'>
                                    <div role='button' tabIndex='0' onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setShowMap(!showMap); }}} className='map' onClick={() => { setShowMap(!showMap) }}>{translator.translate('sandhills.tradesites.mvc.viewonmap')}<i className='fas fa-external-link-alt' /></div>
                                    </span>
                                }

                            {props.DocumentList && props.DocumentList.length > 0 &&
                                    <>
                                        {props.DocumentList.map((doc, index) => (
                                            <span className='upcoming-links link'  key={index}>
                                                {doc.EventFileType.trim() === 'Supplement Sheet' &&
                                                    <a href={doc.FileUrl} target="_blank" aria-label={translator.translate('sandhills.tradesites.mvc.viewsupplementsheet') + " (" + translator.translate('sandhills.opensinanewtab') + ")"}><TranslationText tKey='sandhills.tradesites.mvc.viewsupplementsheet' /><i className='fas fa-external-link-alt' /></a>
                                                }
                                                {doc.EventFileType.trim() === 'Sale Catalog' &&
                                                    <a href={doc.FileUrl} target="_blank" aria-label={translator.translate('sandhills.tradesites.mvc.viewsalecatalog') + " (" + translator.translate('sandhills.opensinanewtab') + ")"}><TranslationText tKey='sandhills.tradesites.mvc.viewsalecatalog' /><i className='fas fa-external-link-alt' /></a>
                                                }
                                            </span>
                                        ))}
                                    </>
                                }
                                {props.isConsignmentPackage &&
                                    <span className='details-link link'>
                                    <div tabIndex='0' className='details' onClick={() => setShowConsignmentDetails(!showConsignmentDetails)}>{translator.translate('sandhills.tradepubwebsite.mvc.contact')} {translator.translate('sandhills.tradepubwebsite.mvc.details')}<i className='fas fa-external-link-alt' />&nbsp;&nbsp;</div>
                                    </span>
                                }
                                </div>
                        </>
                    }
                </div>

                {hasDealerButtons() &&
                    <div className={props.LogoUrl ? 'button-area' : 'button-area bl' }>
                        <>
                            {props.EventID &&
                                <>
                                    {isSavedToWatchList ?
                                        <>
                                            <div className='watchlist-stuff'>
                                            <div role='button' tabIndex='0' onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); removeFromWatchlist(); }}} aria-label={translator.translate('sandhills.removefromwatchlistwithspace')} className='save-link' onClick={(e) => removeFromWatchlist()}>
                                                    <i className='fas fa-heart saved' />&nbsp;
                                                </div>
                                            </div>
                                        </>
                                        :
                                        <>
                                            <div className='watchlist-stuff'>
                                            <div role='button' tabIndex='0' onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); addToWatchlist(); }}} aria-label={translator.translate('sandhills.addtowatchlistwithspace')} className='save-link' onClick={(e) => addToWatchlist()}>
                                                    <i className='fas fa-heart' />&nbsp;
                                                </div>
                                            </div>
                                        </>
                                    }</>
                            }
                            {props.BilledUser &&
                                <a className='dealer-button-consignment-link' href={props.AuctioneerConsignmentLink}>{translator.translate('sandhills.sellwithus')}</a>}     
                            
                            {props.IsLiveOrUpcomingEvent &&
                                <Button className='dealer-button terms-and-conditions' onClick={loadEventBiddingAggreement}>{translator.translate('sandhills.viewtermsandconditions')}</Button>}
                            {props.CompanyURL &&
                                <a className='dealer-button' href={props.CompanyURL}>{props.CompanyInventoryText}</a>}
                            {props.OtherLocations && props.OtherLocations.length > 0 &&
                                <a className='dealer-button' role='button' tabIndex='0' onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setShowLocations(!showLocations); }}} onClick={() => { setShowLocations(!showLocations) }}>{props.parts ? translator.translate('sandhills.tradepubwebsite.mvc.viewotherlocations') : props.LocationInventoryText}</a>}
                            {props.WebsiteURL &&
                                <a className='dealer-button' href={props.WebsiteURL} target='_blank' aria-label={translator.translate('sandhills.tradepubwebsite.mvc.visitourwebsite') + " (" + translator.translate('sandhills.opensinanewtab') + ")"}>{translator.translate('sandhills.tradepubwebsite.mvc.visitourwebsite')}</a>
                            }                            
                            {props.LiveEventUrl &&
                                <a className='dealer-button live-event-button' href={props.LiveEventUrl}>{translator.translate('sandhills.tradepubwebsite.mvc.enterliveauction')}</a>}

                            {props.DealerAuctionTimeBoardUrl &&
                                <a className='dealer-button live-event-button' href={props.DealerAuctionTimeBoardUrl}>{translator.translate('sandhills.tradepubwebsite.mvc.liveauctionboard')}</a>}

                            {props.ConsignmentPackageAuctionTimeBoardUrl &&
                                <a className='dealer-button live-event-button' href={props.ConsignmentPackageAuctionTimeBoardUrl}>{translator.translate('sandhills.tradepubwebsite.mvc.liveauctionboard')}</a>}

                            {props.StandaloneAuctionBoardUrl &&
                                <a className='dealer-button live-event-button' href={props.StandaloneAuctionBoardUrl}>{translator.translate('sandhills.tradepubwebsite.mvc.liveauctionboard')}</a>}
                        </>
                    </div>}
                {
                    windowSize.isMobile && props.LogoUrl &&
                    <div className='dealer-image'>
                        <img src={props.LogoUrl} alt={props.Name} />
                        {props.AuctioneerInformationUrl &&
                            <a className='auctioneer-details' href={props.AuctioneerInformationUrl}>{props.Name}</a>}
                    </div>
                }
            </div>
            {showDescription &&
                <DescriptiveTextModal toggleModal={() => { setShowDescription(!showDescription) }} onClose={() => { setShowDescription(false) }} Description={props.CompanyDescription || props.Description} />
            }
            {showMap &&
                <GoodleMapsLocationModal
                    {...props}
                    onClose={() => { setShowMap(false) }}
                    toggleModal={() => { setShowMap(!showMap) }}
                />
            }
            {showLocations &&
                <DealerLocationsModal toggleModal={() => { setShowLocations(!showLocations) }} onClose={() => { setShowLocations(false) }} Locations={props.OtherLocations} Name={props.Name} />
            }
            {showAuctionInfo &&
                <AuctionInformationModal toggleModal={() => { setShowAuctionInfo(!showAuctionInfo) }} onClose={() => { setShowAuctionInfo(false) }} {...props} />
            }
            {showConsignmentDetails &&
                <ConsignmentPackageInfoModal toggleModal={() => { setShowConsignmentDetails(!showConsignmentDetails) }} {...props} />
            }
        </span>
    );
};

export default AdditionalPageInformation;